import { supabase, getSupabaseUrl } from '../supabase/client';
import { hasTurtleImport, stripTurtleImport, injectTurtleModule, resetTurtleState, extractTurtleSvg, runPythonWithTurtle } from './turtleModule';
import { translateErrorToChinese, getInputLineOffset } from '../utils/errorLineAdjust';

export interface PythonTestCase {
  id: string;
  input: string;
  expectedOutput: string;
  isHidden: boolean;
  scoreWeight: number;
}

export interface TestResult {
  testCaseId: string;
  passed: boolean;
  actualOutput: string;
  expectedOutput: string;
  executionTimeMs: number;
  error?: string;
}

export interface JudgeResult {
  success: boolean;
  results: TestResult[];
  totalScore: number;
  maxScore: number;
  passedCount: number;
  totalCount: number;
  executionTimeMs: number;
  memoryUsedMb: number;
  error?: string;
}

export interface PythonQuestion {
  id: string;
  paper_id: string;
  content: string;
  starter_code: string;
  reference_solution: string;
  time_limit_ms: number;
  memory_limit_mb: number;
  score: number;
  difficulty: string;
}

export interface PythonSubmission {
  id: string;
  user_id: string;
  question_id: string;
  paper_id: string;
  code: string;
  status: 'pending' | 'judging' | 'accepted' | 'wrong_answer' | 'time_limit_exceeded' | 'memory_limit_exceeded' | 'runtime_error';
  score: number;
  execution_time_ms: number;
  memory_used_mb: number;
  test_results: TestResult[];
  submitted_at: string;
}

let pyodideInstance: any = null;
let pyodideLoading: Promise<any> | null = null;

export async function initPyodide() {
  if (pyodideInstance) return pyodideInstance;
  if (pyodideLoading) return pyodideLoading;

  pyodideLoading = new Promise(async (resolve, reject) => {
    try {
      if ((window as any).loadPyodide) {
        pyodideInstance = await (window as any).loadPyodide({
          indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.26.2/full/',
        });
        resolve(pyodideInstance);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/pyodide/v0.26.2/full/pyodide.js';
      script.onload = async () => {
        try {
          const loadPyodide = (window as any).loadPyodide;
          if (!loadPyodide) {
            reject(new Error('loadPyodide not found on window'));
            return;
          }
          pyodideInstance = await loadPyodide({
            indexURL: 'https://cdn.jsdelivr.net/pyodide/v0.26.2/full/',
          });
          resolve(pyodideInstance);
        } catch (err) {
          reject(err);
        }
      };
      script.onerror = () => reject(new Error('Failed to load Pyodide script'));
      document.head.appendChild(script);
    } catch (error) {
      reject(error);
    }
  });

  return pyodideLoading;
}

export async function runPythonLocal(
  code: string,
  input: string = ''
): Promise<{ output: string; error?: string; svg?: string | null }> {
  try {
    const pyodide = await initPyodide();

    if (hasTurtleImport(code)) {
      await injectTurtleModule(pyodide);
      await resetTurtleState(pyodide);
      const strippedCode = stripTurtleImport(code);

      let stdout = '';
      pyodide.setStdout({ batched: (text: string) => { stdout += text + '\n'; } });
      pyodide.setStderr({ batched: (text: string) => { stdout += text + '\n'; } });

      if (input) {
        pyodide.globals.set('input_data', input);
        const wrappedCode = `
import sys
from io import StringIO
sys.stdin = StringIO(input_data)
${strippedCode}
`;
        await pyodide.runPythonAsync(wrappedCode);
      } else {
        await pyodide.runPythonAsync(strippedCode);
      }

      const svgResult = await extractTurtleSvg(pyodide);

      return { output: stdout.trim(), svg: svgResult };
    }

    let stdout = '';
    pyodide.setStdout({ batched: (text: string) => { stdout += text + '\n'; } });
    pyodide.setStderr({ batched: (text: string) => { stdout += text + '\n'; } });

    if (input) {
      pyodide.globals.set('input_data', input);
      const wrappedCode = `
import sys
from io import StringIO
sys.stdin = StringIO(input_data)
${code}
`;
      await pyodide.runPythonAsync(wrappedCode);
    } else {
      await pyodide.runPythonAsync(code);
    }

    return { output: stdout.trim() };
  } catch (error: any) {
    const lineOffset = getInputLineOffset(Boolean(input));
    const translatedError = translateErrorToChinese(error.message || '执行失败', lineOffset);
    return { output: '', error: translatedError };
  }
}

export async function judgePythonCode(
  code: string,
  testCases: PythonTestCase[],
  timeLimitMs: number = 1000,
  memoryLimitMb: number = 64
): Promise<JudgeResult> {
  try {
    const session = (await supabase.auth.getSession()).data.session;
    const authHeaders = session ? { Authorization: `Bearer ${session.access_token}` } : {};

    const response = await fetch(`${getSupabaseUrl()}/functions/v1/code-grading`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authHeaders as Record<string, string>),
      },
      body: JSON.stringify({
        action: 'python_judge',
        code,
        testCases,
        timeLimitMs,
        memoryLimitMb,
      }),
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.error || '判题请求失败');
    }

    return result as JudgeResult;
  } catch (error: any) {
    return {
      success: false,
      results: [],
      totalScore: 0,
      maxScore: 0,
      passedCount: 0,
      totalCount: testCases.length,
      executionTimeMs: 0,
      memoryUsedMb: 0,
      error: error.message || '判题服务异常',
    };
  }
}

export async function getPythonQuestion(questionId: string): Promise<PythonQuestion | null> {
  const { data, error } = await supabase
    .from('python_questions')
    .select('*')
    .eq('id', questionId)
    .maybeSingle();

  if (error) {
    
    return null;
  }

  return data as PythonQuestion;
}

export async function getPythonTestCases(questionId: string): Promise<PythonTestCase[]> {
  const { data, error } = await supabase
    .from('python_test_cases')
    .select('*')
    .eq('question_id', questionId)
    .order('sort_order', { ascending: true });

  if (error) {
    
    return [];
  }

  return (data || []).map((tc: any) => ({
    id: tc.id,
    input: tc.input_data || '',
    expectedOutput: tc.expected_output,
    isHidden: tc.is_hidden || false,
    scoreWeight: tc.score_weight || 1,
  }));
}

export async function savePythonSubmission(
  submission: Partial<PythonSubmission>
): Promise<PythonSubmission | null> {
  const { data, error } = await supabase
    .from('python_submissions')
    .insert(submission as any)
    .select()
    .maybeSingle();

  if (error) {
    
    return null;
  }

  return data as unknown as PythonSubmission;
}

export async function getPythonSubmissions(
  userId: string,
  questionId?: string
): Promise<PythonSubmission[]> {
  let query = supabase
    .from('python_submissions')
    .select('*')
    .eq('user_id', userId)
    .order('submitted_at', { ascending: false });

  if (questionId) {
    query = query.eq('question_id', questionId);
  }

  const { data, error } = await query;

  if (error) {
    
    return [];
  }

  return (data || []) as unknown as PythonSubmission[];
}

export function getStatusText(status: string): string {
  const statusMap: Record<string, string> = {
    pending: '等待判题',
    judging: '判题中',
    accepted: '通过',
    wrong_answer: '答案错误',
    time_limit_exceeded: '超时',
    memory_limit_exceeded: '内存超限',
    runtime_error: '运行时错误',
  };
  return statusMap[status] || status;
}

export function getStatusColor(status: string): string {
  const colorMap: Record<string, string> = {
    pending: 'text-gray-500',
    judging: 'text-blue-500',
    accepted: 'text-green-500',
    wrong_answer: 'text-red-500',
    time_limit_exceeded: 'text-orange-500',
    memory_limit_exceeded: 'text-orange-500',
    runtime_error: 'text-red-500',
  };
  return colorMap[status] || 'text-gray-500';
}
